import React, { useState } from 'react';
import styles from './CustomFilterModal.module.css';
import SelectType, { TItem } from './SelectType';

// Ключи групп
type GroupKey =
  | 'new'
  | 'in_progress'
  | 'dispute'
  | 'success'
  | 'recalculations'
  | 'errors'
  | 'canceled_grp'
  | 'support';

export type FilterValues = {
  type: 'all' | 'deposit' | 'withdrawal';
  // можно выбрать группу или "Все"
  status: GroupKey | 'all';
  amountFrom: number | '';
  amountTo: number | '';
  dateFrom: string;
  dateTo: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
};

// Опции выбора типа заявки
const typeOptions: TItem<'all' | 'deposit' | 'withdrawal'>[] = [
  { id: 'all',        name: 'Все' },
  { id: 'deposit',    name: 'Пополнение' },
  { id: 'withdrawal', name: 'Выплата' },
];

// Определяем группы статусов для каждого типа
const STATUS_GROUPS: Record<FilterValues['type'], { key: GroupKey; label: string }[]> = {
  all: [
    { key: 'new',           label: 'Новые' },
    { key: 'in_progress',   label: 'В процессе' },
    { key: 'dispute',       label: 'Споры' },
    { key: 'success',       label: 'Успех' },
    { key: 'recalculations',label: 'Перерасчёты' },
    { key: 'errors',        label: 'Ошибки/таймаут' },
    { key: 'canceled_grp',  label: 'Отменено' },
    { key: 'support',       label: 'Поддержка' },
  ],
  deposit: [
    { key: 'new',         label: 'Idle (frozen)' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'success',     label: 'Success' },
    { key: 'errors',      label: 'Failed' },
  ],
  withdrawal: [
    { key: 'new',         label: 'Новые' },
    { key: 'in_progress', label: 'В процессе' },
    { key: 'support',     label: 'Поддержка' },
    { key: 'success',     label: 'Успешно' },
    { key: 'canceled_grp',label: 'Отменено' },
    { key: 'dispute',     label: 'В споре' },
  ],
};

// Начальные значения фильтра
const defaultFilter: FilterValues = {
  type: 'all',
  status: 'all',
  amountFrom: '',
  amountTo: '',
  dateFrom: '',
  dateTo: '',
};

const CustomFilterModal: React.FC<Props> = ({ isOpen, onClose, onApply }) => {
  const [filter, setFilter] = useState<FilterValues>(defaultFilter);
  const change = <K extends keyof FilterValues>(field: K, value: FilterValues[K]) =>
    setFilter(prev => ({ ...prev, [field]: value }));

  // Генерируем элементы статусов: "Все" + группы (без кодов)
  const statusItems: TItem<FilterValues['status']>[] = [
    { id: 'all', name: 'Все' },
    ...STATUS_GROUPS[filter.type].map(group => ({ id: group.key, name: group.label })),
  ];

  if (!isOpen) return null;

  // Проверка диапазонов
  const invalid =
    (!!(filter.amountFrom !== '' && filter.amountTo !== '' && filter.amountFrom > filter.amountTo)) ||
    (!!(filter.dateFrom && filter.dateTo && filter.dateFrom > filter.dateTo));

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>Пользовательский фильтр</h3>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.subtitle}>Установите необходимые параметры</div>
        <form className={styles.form} onSubmit={e => { e.preventDefault(); if (!invalid) onApply(filter); }}>
          <SelectType<'all' | 'deposit' | 'withdrawal'>
            title="Направление"
            items={typeOptions}
            value={filter.type}
            onChange={v => change('type', v)}
          />

          <SelectType<FilterValues['status']>
            title="Статус"
            items={statusItems}
            value={filter.status}
            onChange={v => change('status', v)}
          />

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Сумма от</label>
              <input
                type="number"
                className={styles.input}
                min="0"
                step="0.01"
                value={filter.amountFrom}
                onChange={e => change('amountFrom', e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="0"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Сумма до</label>
              <input
                type="number"
                className={styles.input}
                min="0"
                step="0.01"
                value={filter.amountTo}
                onChange={e => change('amountTo', e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="0"
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Дата с</label>
              <input
                type="date"
                className={styles.input}
                value={filter.dateFrom}
                onChange={e => change('dateFrom', e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Дата по</label>
              <input
                type="date"
                className={styles.input}
                value={filter.dateTo}
                onChange={e => change('dateTo', e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className={styles.button} disabled={invalid}>
            Применить
          </button>
        </form>
      </div>
    </>
  );
};

export default CustomFilterModal;
